/**
 * 扣柜原因的措辞和颜色 —— 柜子监控弹窗的 Holds 那一列和派单/柜子管理列表的
 * On Hold 那一行共用这一套，否则同一只柜在两个界面上会有两种说法。
 *
 * 画出来的那个组件在 components/shared/HoldReasons.tsx。
 */
import type { ContainerHoldDetail } from '../types/scraper'

export const holdAbbr: Record<string, string> = {
  customs: 'Cust.',
  carrier: 'Line',
  terminal: 'Term.',
  cbpa: 'CBPA',
  ctf: 'CTF',
  tmf: 'TMF',
  freight: 'Freight',
  demurrage: 'Demur.',
  exam: 'Exam',
  'not-at-terminal': 'Off-dock',
  'on-vessel': 'On vessel',
  // 码头正在挪柜子。原先这一类读不出来，整行落到 ⚠️ Data 那个兜底标签上。
  'in-processing': 'Moving',
  inconsistent: '⚠️ Data',
}

// Colour by how the dispatcher has to act on it: government/customs holds are
// the ones nobody can clear quickly, fee holds are self-service, and the
// "it isn't here" categories aren't holds at all.
export const holdCategoryStyle: Record<string, string> = {
  customs: 'bg-red-50 text-red-700 border-red-200',
  cbpa: 'bg-red-50 text-red-700 border-red-200',
  exam: 'bg-red-50 text-red-700 border-red-200',
  carrier: 'bg-orange-50 text-orange-700 border-orange-200',
  terminal: 'bg-amber-50 text-amber-800 border-amber-200',
  ctf: 'bg-amber-50 text-amber-800 border-amber-200',
  tmf: 'bg-amber-50 text-amber-800 border-amber-200',
  freight: 'bg-orange-50 text-orange-700 border-orange-200',
  demurrage: 'bg-orange-50 text-orange-700 border-orange-200',
  'not-at-terminal': 'bg-slate-50 text-slate-600 border-slate-200',
  'on-vessel': 'bg-slate-50 text-slate-600 border-slate-200',
  // 和「不在这儿」同色：不是扣柜，没人需要去清它，等就完了。
  'in-processing': 'bg-slate-50 text-slate-600 border-slate-200',
  inconsistent: 'bg-slate-100 text-slate-500 border-slate-300',
}

export function formatHolds(holds: string[] | null): string {
  if (!holds || holds.length === 0) return '-'
  return holds.map(h => holdAbbr[h] || h).join(', ')
}

/**
 * 窄栏里用的短说法 —— 派单/柜子管理列表的 On Hold 行只有一列宽（130px），
 * 码头的原话放不下：`NOT AVAILABLE FOR PICKUP` 会被切成
 * `NOT AVAILABLE FOR PI…`，一行柜子占掉三行高，看的人还是不知道是哪一类。
 *
 * 规矩：**先看类别**（类别是我们自己归好的，天生就短），只有 `terminal` 这个
 * 自由文本的兜底类别才去读原话。原话一律不截断 —— 截一半的句子比短词更难懂。
 * 完整原话没有丢，还在鼠标停上去的那几行里。
 */
const SHORT_BY_CATEGORY: Record<string, string> = {
  customs: 'Customs',
  cbpa: 'CBPA',
  exam: 'Exam',
  carrier: 'Line',
  ctf: 'CTF',
  tmf: 'TMF',
  freight: 'Freight',
  demurrage: 'Demurrage',
  'gate-fee': 'Gate fee',
  'closed-area': 'Closed area',
  'in-processing': 'Moving',
  'not-at-terminal': 'Off-dock',
  'on-vessel': 'On vessel',
  inconsistent: '⚠️ Data',
}

/**
 * `terminal` 那一类的原话 → 短词。按出现频率排，第一个命中的算数。
 *
 * ⚠️ 顺序有讲究：`Closed AreaGate Fee Due` 这种粘在一起的存量（2026-10-08 之前
 * 写下的）两条都能命中，先匹配到的是封闭区 —— 那是两条里更要紧的一条（封闭区
 * 付了钱也提不出来），所以封闭区必须排在闸口费前面。
 */
const SHORT_BY_TEXT: Array<[RegExp, string]> = [
  [/closed\s*area/i, 'Closed area'],
  [/exam/i, 'Exam'],
  [/customs/i, 'Customs'],
  [/line hold|contact ssl|carrier/i, 'Line'],
  [/gate\s*fee/i, 'Gate fee'],
  [/clean truck/i, 'CTF'],
  [/traffic mitigation/i, 'TMF'],
  [/demurrage/i, 'Demurrage'],
  [/freight/i, 'Freight'],
  [/in\s*processing/i, 'Moving'],
  // 「提不出来」码头有好几种写法（`NOT AVAILABLE FOR PICKUP`、
  // `Import Not Accessible`），对调度员是同一件事，归成同一个短词 —— 顺带把
  // 一行上的重复说法合并掉。
  [/not\s*available|not\s*accessible|unavailable/i, 'Not available'],
  // TraPac 的通用那一条。排最后：上面任何一条更具体的命中都该赢过它。
  [/terminal hold/i, 'Terminal'],
];

/** 窄栏里显示的短说法；认不出来的回退到类别缩写，再不行才用原话。 */
export function shortHoldLabel(d: ContainerHoldDetail): string {
  const byCategory = SHORT_BY_CATEGORY[d.category]
  if (byCategory) return byCategory
  for (const [re, short] of SHORT_BY_TEXT) {
    if (re.test(d.label)) return short
  }
  return holdAbbr[d.category] || d.label
}

/**
 * 短词去重，保持原顺序；颜色跟着第一次出现的那条理由的类别走。
 *
 * 同一只柜经常被同一件事说两遍（码头写 `Gate Fee Due`，我们自己又归出一条
 * 「只差一笔闸口费」），缩短之后两条长得一模一样 —— 不去重就是同一个词并排
 * 占两格。线上实样里这种重复占了小一半。
 */
export function shortHoldChips(
  details: ContainerHoldDetail[],
): Array<{ label: string; category: string }> {
  const seen = new Set<string>()
  const out: Array<{ label: string; category: string }> = []
  for (const d of details) {
    const label = shortHoldLabel(d)
    if (seen.has(label)) continue
    seen.add(label)
    out.push({ label, category: d.category })
  }
  return out
}

/** One line per reason, used as the hover text and in the history drawer. */
export function describeHoldDetail(d: ContainerHoldDetail): string {
  const category = holdAbbr[d.category] || d.category
  const code = d.code && d.code !== d.label ? ` [${d.code}]` : ''
  const detail = d.detail ? ` — ${d.detail}` : ''
  return `${category}: ${d.label}${code}${detail}`
}
